import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/safeSupabase';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Calculator } from 'lucide-react';
import { toast } from 'sonner';
import { sanitizeError } from '@/lib/errorMessages';
import { validatePassword } from '@/lib/passwordValidation';
import { checkRateLimit, getRetryAfter } from '@/lib/rateLimiter';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;

    // Implicit flow: recovery token in hash fragment
    if (hash.includes('type=recovery')) {
      setReady(true);
      return;
    }

    // PKCE flow: code in query params
    const params = new URLSearchParams(search);
    const code = params.get('code');
    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (error) {
          toast.error('Error', { description: sanitizeError(error) });
        } else {
          setReady(true);
        }
      });
      return;
    }

    // Also listen for auth state change (Supabase client auto-processes hash)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkRateLimit('auth:reset-pw', 3, 60_000)) {
      const wait = getRetryAfter('auth:reset-pw', 3, 60_000);
      toast.error('Too many attempts', { description: `Please wait ${wait} seconds before trying again.` });
      return;
    }
    const pw = validatePassword(password);
    if (!pw.valid) {
      toast.error('Weak password', { description: pw.errors.join('. ') });
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) {
      toast.error('Error', { description: sanitizeError(error) });
    } else {
      toast.success('Password updated', { description: 'You can now sign in with your new password.' });
      navigate('/');
    }
  };

  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="py-16 text-center text-muted-foreground">
            <p>Invalid or expired reset link.</p>
            <Button variant="link" onClick={() => navigate('/auth')}>Back to login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Calculator className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">Set New Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <PasswordInput value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required minLength={12} />
              {password.length > 0 && !validatePassword(password).valid && (
                <p className="text-xs text-destructive mt-1">
                  {validatePassword(password).errors.join('. ')}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
